import { useMediaQuery } from '@mui/material';

// Unchanged from the panel's original fixed width.
export const ASSISTANT_PANEL_WIDTH = 520;

// Minimum usable width for the main content column beside the panel —
// aligned with MUI's own default 'sm' breakpoint, not an arbitrary number.
export const ASSISTANT_MIN_CONTENT_WIDTH = 600;

/** Viewport width at which the panel can sit beside the sidebar and the page. */
export function assistantSideBySideMinWidth(sidebarWidth: number): number {
  return sidebarWidth + ASSISTANT_PANEL_WIDTH + ASSISTANT_MIN_CONTENT_WIDTH;
}

/**
 * Pure side-by-side check. `viewportWidth` is compared the same way as the
 * `min-width` media query: the panel fits when the viewport is at least the threshold.
 */
export function fitsAssistantSideBySide(sidebarWidth: number, viewportWidth: number): boolean {
  return viewportWidth >= assistantSideBySideMinWidth(sidebarWidth);
}

/**
 * Media query string for "there's enough real room to show the assistant
 * panel side-by-side with the main content." Exported separately from the
 * hook so the threshold arithmetic itself is unit-testable without touching
 * matchMedia/DOM.
 */
export function getAssistantSideBySideQuery(sidebarWidth: number): string {
  return `(min-width:${String(assistantSideBySideMinWidth(sidebarWidth))}px)`;
}

/**
 * Whether the assistant panel can be shown side-by-side with the main
 * content right now. This is a real-available-space check (sidebar + panel
 * + a minimum usable content width), not just a device-class breakpoint —
 * a narrow desktop browser window should fall back to the overlay panel
 * exactly like mobile does, if the numbers don't fit.
 */
export function useAssistantSideBySide(sidebarWidth: number): boolean {
  return useMediaQuery(getAssistantSideBySideQuery(sidebarWidth));
}
